import type { ReactNode } from "react";
import { useQuoteStatusLabel } from "../api/quoteStatusLabels";
import type { QuoteStatus } from "../api/types";

// 徽章一律「顏色 + 文字」,不可只靠顏色表達狀態。
//
// 底色一律是卡片本身的 --surface,不是寫死的白:深色模式下 --ok / --warn /
// --danger 是為深底提亮的淺綠、淺橘、淺粉,壓在寫死的白底上只有 1.40–1.90:1 ——
// 文字讀不出來,狀態就退回只靠顏色,正是這一行要擋的事。

export type BadgeTone = "neutral" | "accent" | "ok" | "warn" | "danger";

const TONES: Record<BadgeTone, string> = {
  neutral: "border-[var(--border-strong)] bg-[var(--surface-2)] text-[var(--text-2)]",
  accent: "border-[var(--accent)] bg-[var(--accent-soft)] text-[var(--accent-hover)]",
  ok: "border-[var(--ok)] bg-[var(--surface)] text-[var(--ok)]",
  warn: "border-[var(--warn)] bg-[var(--surface)] text-[var(--warn)]",
  danger: "border-[var(--danger)] bg-[var(--surface)] text-[var(--danger)]",
};

export interface BadgeProps {
  tone?: BadgeTone;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}

export function Badge({ tone = "neutral", icon, children, className = "" }: BadgeProps) {
  return (
    <span
      className={
        // **不換行。** 業主 2026-09-20:「視窗框度縮小一點 會變形可以修嗎」——
        // 徽章是 rounded-full,圓角半徑取高度的一半,所以「已結案」一折成兩行,
        // 那顆藥丸就腫成一個歪掉的橢圓。
        //
        // 與報價單表頭同一個處理方式(業主 2026-09-17:「被壓縮 很醜 不專業」):
        // 擠不下就讓外層橫捲,不要把字折斷。徽章上的字本來就只有兩三個。
        "inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium " +
        `${TONES[tone]} ${className}`
      }
    >
      {icon}
      {children}
    </span>
  );
}

/**
 * 報價單狀態對應的色調。**文字不在這裡** —— 見 StatusBadge。
 *
 * 分辨狀態靠的本來就是徽章上那幾個字,顏色只是順手的提示
 * (全域規矩:狀態不可只靠顏色)。
 */
const STATUS_TONE: Record<QuoteStatus, BadgeTone> = {
  DRAFT: "neutral",
  SENT: "accent",
  CLOSED: "ok",
  CANCELLED: "danger",
};

export interface StatusBadgeProps {
  status: QuoteStatus;
  className?: string;
}

/**
 * 報價單狀態徽章:草稿 / 已寄出 / 已結案 / 已取消(「未成交」2026-09-30、「已回簽」「發包製作」2026-10-15 拿掉)。
 *
 * **字樣是後端給的**(設計規格 §1.4:後端是唯一來源)。徽章才是使用者天天看
 * 狀態的地方 —— 漏斗選項讀 API、同一列的徽章讀前端寫死的一份,哪天後端改一個
 * 措辭(這一輪就把 CANCELLED 從「已作廢」改成「已取消」),同一個畫面上兩處
 * 會顯示不同的字,而兩套測試都是綠的。
 *
 * 還沒載到字樣時印識別字本身(`DRAFT`),後端多一個狀態值時也是 ——
 * **絕不印空白**:那會讓狀態退回只剩顏色。
 *
 * 色調查不到就退回 neutral,理由相同:一個沒有邊框底色的徽章仍然讀得出字,
 * 一個 `undefined` 的 class 會把整顆徽章畫壞。
 */
export function StatusBadge({ status, className }: StatusBadgeProps) {
  const label = useQuoteStatusLabel(status);
  return (
    <Badge tone={STATUS_TONE[status] ?? "neutral"} className={className}>
      {label}
    </Badge>
  );
}
